import { LogOut, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CollapsibleSettingsMenu from "./CollapsibleMenu";

import { useAuth } from "@/common/hooks/useAuth";
import { usePermissions } from "@/common/hooks/usePermissions";
import type { MenuItem } from "@/common/types/MenuItems";
import { isPathActive } from "@/common/utils/utils";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

interface SidebarItemProps {
  item: MenuItem;
  collapsed: boolean;
  isSubmenuItem?: boolean;
}

export function LogoutConfirmDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onCancel}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white rounded-2xl shadow-[0_24px_64px_rgba(15,23,42,0.18)] border border-ink-line overflow-hidden">
        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 shrink-0">
                <LogOut size={18} strokeWidth={1.75} />
              </span>
              <div>
                <p
                  id="logout-dialog-title"
                  className="text-[15px] font-semibold text-slate-900 leading-snug"
                >
                  Sign out
                </p>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  You can sign back in anytime
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-slate-400 hover:text-slate-600 transition-colors rounded-md p-0.5 -mt-0.5"
              aria-label="Cancel"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-[13px] text-slate-600 leading-relaxed mb-5">
            Any unsaved work will remain intact. Your session and preferences
            are preserved for next time.
          </p>

          {/* Actions */}
          <div className="flex gap-2.5">
            <button
              onClick={onCancel}
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Stay signed in
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-black transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const SidebarNavItem: React.FC<SidebarItemProps> = ({
  item,
  collapsed,
  isSubmenuItem: _isSubmenuItem,
}) => {
  const isCollapsible = useMemo(() => !!item.submenu, [item]);
  const location = useLocation();
  const { logoutUser } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const isActive = isPathActive(
    item.path,
    location.pathname,
    item.matchPattern,
  );
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Hide if the user lacks the required permission for this item
  if (
    item.requiredPermission &&
    !can(
      item.requiredPermission.resource as any,
      item.requiredPermission.action as any,
    )
  ) {
    return null;
  }

  if (isCollapsible) {
    return <CollapsibleSettingsMenu item={item} collapsed={collapsed} />;
  }

  const handleClick = () => {
    if (item.path === "/logout") {
      setShowLogoutDialog(true);
    } else {
      navigate(item?.path);
    }
  };

  return (
    <>
      {showLogoutDialog && (
        <LogoutConfirmDialog
          onConfirm={() => {
            setShowLogoutDialog(false);
            logoutUser();
          }}
          onCancel={() => setShowLogoutDialog(false)}
        />
      )}
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.label}
          isActive={isActive}
          onClick={handleClick}
          data-tour={`sidebar-${item.label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "")}`}
        >
          {item.icon && <item.icon size={16} />}
          <span>{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </>
  );
};

export default SidebarNavItem;
